import { api } from "@/features/utils/fetch-server";
import type { Faq } from "../types";

export class FaqAPI {
  // List FAQs (optionally only those needing refinement)
  static listFaqs = async (
    clientId: string,
    options?: { status?: string; needsRefinement?: boolean },
  ): Promise<{ faqs: Faq[]; total: number }> => {
    const params = new URLSearchParams();
    if (options?.status) params.append("status", options.status);
    if (options?.needsRefinement) params.append("needs_refinement", "true");

    const query = params.toString() ? `?${params}` : "";
    return api.get<{ faqs: Faq[]; total: number }>(
      `/api/faq/v1/${clientId}/faqs${query}`,
    );
  };

  // Get a single FAQ
  static getFaq = async (clientId: string, id: string): Promise<{ faq: Faq }> => {
    return api.get<{ faq: Faq }>(`/api/faq/v1/${clientId}/faqs/${id}`);
  };

  // Create a FAQ manually
  static createFaq = async (
    clientId: string,
    data: {
      question: string;
      refined_response?: string;
      category?: string;
      variations?: string[];
    },
  ): Promise<{ faq: Faq }> => {
    return api.post<{ faq: Faq }, typeof data>(
      `/api/faq/v1/${clientId}/faqs`,
      data,
    );
  };

  // Update / refine a FAQ
  static updateFaq = async (
    clientId: string,
    id: string,
    data: Partial<
      Pick<
        Faq,
        "question" | "refined_response" | "category" | "variations" | "status"
      >
    >,
  ): Promise<{ faq: Faq }> => {
    return api.put<{ faq: Faq }, typeof data>(
      `/api/faq/v1/${clientId}/faqs/${id}`,
      data,
    );
  };

  // Delete a FAQ
  static deleteFaq = async (
    clientId: string,
    id: string,
  ): Promise<{ success: boolean }> => {
    return api.delete<{ success: boolean }>(`/api/faq/v1/${clientId}/faqs/${id}`);
  };

  // Detect duplicate FAQ clusters (fuzzy)
  static listDuplicates = async (
    clientId: string,
  ): Promise<{ groups: Faq[][]; total: number }> => {
    return api.get<{ groups: Faq[][]; total: number }>(
      `/api/faq/v1/${clientId}/faqs/duplicates`,
    );
  };

  // Merge source FAQs into a target FAQ
  static mergeFaqs = async (
    clientId: string,
    targetId: string,
    sourceIds: string[],
  ): Promise<{ faq: Faq }> => {
    return api.post<{ faq: Faq }, { source_ids: string[] }>(
      `/api/faq/v1/${clientId}/faqs/${targetId}/merge`,
      { source_ids: sourceIds },
    );
  };
}
